import toast from "react-hot-toast";
import axiosSetup from "../utils/axiosSetup";
import { ColumnPreference } from "../models/LeadData.model";

export const getLeadsByStatus = async (
  status: string,
  page: number = 1,
  limit: number = 50,
  filters?: {
    assignee?: string;
    sourceType?: string;
    date?: string;
    state?: string;
    district?: string;
    city?: string;
    pincode?: string;
    searchQuery?: string;
    category?: string;
  },
) => {
  try {
    const params = new URLSearchParams();

    params.append("status", status);
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (filters?.assignee) params.append("assignee", filters.assignee);
    if (filters?.sourceType) params.append("sourceType", filters.sourceType);
    if (filters?.date) params.append("date", filters.date);
    if (filters?.state) params.append("state", filters.state);
    if (filters?.district) params.append("district", filters.district);
    if (filters?.city) params.append("city", filters.city);
    if (filters?.pincode) params.append("pincode", filters.pincode);
    if (filters?.searchQuery) params.append("searchQuery", filters.searchQuery);
    if (filters?.category) params.append("category", filters.category);
    const response = await axiosSetup.get(
      `/leads/by-status?${params.toString()}`,
    );

    return {
      leads: response.data.data?.leads || [],
      totalCount: response.data.data?.count || 0,
      hasMore: response.data.data?.leads?.length === limit,
      page,
      limit,
    };
  } catch (error: any) {
    console.error("Error fetching leads by status:", error);
    throw error;
  }
};
export const getLeads = async (
  page: number = 1,
  limit: number = 50,
  filters?: {
    assignee?: string;
    sourceType?: string;
    date?: string;
    state?: string;
    district?: string;
    city?: string;
    pincode?: string;
    searchQuery?: string;
  },
) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (filters?.assignee) params.append("assignee", filters.assignee);
    if (filters?.sourceType) params.append("sourceType", filters.sourceType);
    if (filters?.date) params.append("date", filters.date);
    if (filters?.state) params.append("state", filters.state);
    if (filters?.district) params.append("district", filters.district);
    if (filters?.city) params.append("city", filters.city);
    if (filters?.pincode) params.append("pincode", filters.pincode);
    if (filters?.searchQuery) params.append("searchQuery", filters.searchQuery);
    const response = await axiosSetup.get(`/leads?${params.toString()}`);

    return {
      leads: response.data.data?.leads || [],
      totalCount: response.data.data?.count || 0,
      hasMore: response.data.data?.leads?.length === limit,
      page,
      limit,
    };
  } catch (error: any) {
    console.error("Error fetching leads by status:", error);
    throw error;
  }
};

export const fetchAssigneeData = async () => {
  try {
    const response = await axiosSetup.get("/leads/getLeadsAssignee");
    return response.data.data;
  } catch (error: any) {
    toast.error("Error fetching kanban boards:", error);
  }
};

export const fetchAssigneeLogs = async (leadId: string) => {
  try {
    const response = await axiosSetup.get(`/leads/assigneeLogs/${leadId}`);
    return response.data.data;
  } catch (error: any) {
    toast.error("Error fetching assignee logs:", error);
    throw error;
  }
};

export const getLeadLogCount = async (date?: string) => {
  try {
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    const response = await axiosSetup.get(
      `/leads/getLeadLogs?${params.toString()}`,
    );
    return response.data.data.count;
  } catch (error: any) {
    toast.error("Error fetching today's log count: " + (error?.message || ""));
    throw error;
  }
};

export const getDataEntryLeadCount = async (
  startDate?: string,
  endDate?: string,
) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const response = await axiosSetup.get(
      `/leads/get-dataentry-lead-log-count?${params.toString()}`,
    );

    return response.data.data;
  } catch (error) {
    console.error("Error fetching data entry lead logs:", error);
    throw error;
  }
};

export const createKanbanBoard = async (boardData: any) => {
  try {
    const response = await axiosSetup.post("/leads/add", boardData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating kanban board:", error);
    throw error;
  }
};

export const updateKanbanBoard = async (
  currentStatus: string,
  destinationStatus: string,
  leadId: string,
  destinationIndex: number,
  currentIndex: number,
) => {
  try {
    const response = await axiosSetup.put("/leads/updateLeads", {
      currentStatus,
      destinationStatus,
      leadId,
      destinationIndex,
      currentIndex,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating kanban board:", error);
    throw error;
  }
};

export const deleteKanbanLeadApi = async (leadId: string) => {
  const response = await axiosSetup.delete(`/leads/deleteLead/${leadId}`);
  return response.data;
};

export const updateSourceType = async (leadId: string, sourceType: string) => {
  try {
    const response = await axiosSetup.put("/leads/updateSourceType", {
      leadId,
      sourceType,
    });
    return response.data.data.type;
  } catch (error) {
    console.error("Error updating source type:", error);
    throw error;
  }
};

export const fetchStatesData = async () => {
  try {
    const response = await axiosSetup.get("/states");
    return response.data.data.states;
  } catch (error: any) {
    toast.error("Error fetching states data: " + (error?.message || ""));
    throw error;
  }
};

export const fetchTalukasByDistrict = async (district: string) => {
  try {
    const response = await axiosSetup.get(`/states?district=${district}`);
    return response.data.data.talukaData;
  } catch (error: any) {
    toast.error("Error fetching talukas: " + (error?.message || ""));
    throw error;
  }
};

export const updateUserColumnPreferences = async (
  preferences: ColumnPreference[],
) => {
  try {
    const response = await axiosSetup.put(
      "/kanbanColumns/update-column-preferences",
      {
        preferences,
      },
    );
    return response.data;
  } catch (error: any) {
    toast.error("Error updating column preferences: " + (error?.message || ""));
    throw error;
  }
};
